import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcUnitRounded,
  AddRounded,
  AutoAwesomeRounded,
  CheckCircleRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  CloseRounded,
  CoffeeRounded,
  DeleteOutlineRounded,
  EditOutlined,
  Inventory2Rounded,
  KitchenRounded,
  NightsStayRounded,
  RestaurantRounded,
  TodayRounded,
  WarningAmberRounded,
  WbSunnyRounded,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { addDays, format, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { useWeeklyMenu } from '../MenuSemanal/useWeeklyMenu';
import AddMealPopup from '../MenuSemanal/AddMealPopup';
import MenuGeneratorIA from '../MenuGeneratorIA';
import BatchCookingModal from '../BatchCooking/BatchCookingModal';
import AIFoodAnalyzer from '../AIFoodAnalyzer';
import FoodStockPage from '../FoodStockPage';
import ExperimentalPopupProvider from '../ExperimentalPopups/ExperimentalPopupProvider';
import './WeeklyPlannerV3.css';

const MEAL_META = {
  Desayuno: { icon: WbSunnyRounded, tone: 'amber', short: 'Desayuno' },
  'Media mañana': { icon: CoffeeRounded, tone: 'violet', short: 'Media mañana' },
  Comida: { icon: RestaurantRounded, tone: 'rose', short: 'Comida' },
  'Media tarde': { icon: CoffeeRounded, tone: 'mint', short: 'Merienda' },
  Cena: { icon: NightsStayRounded, tone: 'blue', short: 'Cena' },
};

const emptyMacros = { kilocalories: 0, protein: 0, carbs: 0, fat: 0 };
const value = (input) => Number(input) || 0;
const getWeeklyGoal = (goals) => ({
  kilocalories: value(goals.kilocalories) * 7,
  protein: value(goals.protein) * 7,
  carbs: value(goals.carbs) * 7,
  fat: value(goals.fat) * 7,
});

const getPreviewImage = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '';
  return `${baseUrl}/${path.replace(/\\/g, '/').replace(/^\//, '')}`;
};

const createPreviewMenu = () => {
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
  const meal = (items = [], macros = emptyMacros) => ({ items, macros });
  const food = (id, name, macros, extra = {}) => ({
    _id: id,
    name,
    isRecipe: true,
    type: 'planned-recipe',
    quantity: 1,
    unit: 'ración',
    ...macros,
    ...extra,
  });
  const catalog = [
    food('preview-1', 'Porridge de avena, plátano y canela', { kilocalories: 390, protein: 17, carbs: 62, fat: 9 }),
    food('preview-2', 'Bowl mediterráneo de pollo', { kilocalories: 610, protein: 44, carbs: 68, fat: 19 }),
    food('preview-3', 'Salmón al horno con verduras', { kilocalories: 540, protein: 39, carbs: 35, fat: 25 }, { stockPrepPortions: 2 }),
    food('preview-4', 'Tostada integral con aguacate', { kilocalories: 310, protein: 12, carbs: 38, fat: 13 }),
    food('preview-5', 'Yogur con frutos rojos', { kilocalories: 190, protein: 14, carbs: 22, fat: 6 }),
  ];

  const weeklyData = {};
  for (let offset = 0; offset < 7; offset += 1) {
    const date = addDays(monday, offset);
    const key = format(date, 'yyyy-MM-dd');
    const first = catalog[offset % catalog.length];
    const second = catalog[(offset + 1) % catalog.length];
    const third = catalog[(offset + 2) % catalog.length];
    const meals = {
      Desayuno: meal(offset === 6 ? [] : [first], offset === 6 ? emptyMacros : first),
      'Media mañana': meal(offset % 2 === 0 ? [catalog[4]] : [], offset % 2 === 0 ? catalog[4] : emptyMacros),
      Comida: meal(offset === 6 ? [] : [second], offset === 6 ? emptyMacros : second),
      'Media tarde': meal([], emptyMacros),
      Cena: meal(offset > 4 ? [] : [third], offset > 4 ? emptyMacros : third),
    };
    const totals = Object.values(meals).reduce((sum, current) => ({
      kilocalories: sum.kilocalories + value(current.macros.kilocalories),
      protein: sum.protein + value(current.macros.protein),
      carbs: sum.carbs + value(current.macros.carbs),
      fat: sum.fat + value(current.macros.fat),
    }), { ...emptyMacros });
    weeklyData[key] = { date, meals, totals };
  }

  const weeklyTotals = Object.values(weeklyData).reduce((sum, day) => ({
    kilocalories: sum.kilocalories + day.totals.kilocalories,
    protein: sum.protein + day.totals.protein,
    carbs: sum.carbs + day.totals.carbs,
    fat: sum.fat + day.totals.fat,
  }), { ...emptyMacros });

  return {
    currentWeekStart: monday,
    weeklyData,
    weeklyTotals,
    userGoals: { kilocalories: 2000, protein: 140, carbs: 230, fat: 70 },
    loading: false,
    error: null,
    actions: {
      prevWeek: () => {}, nextWeek: () => {}, goToToday: () => {}, refresh: async () => {},
      addFoodToMeal: async () => {}, removeFoodFromMeal: async () => {}, makeRecipeStock: async () => {},
    },
  };
};

const PlannerHeader = ({ onGenerate, onStock, onBatch }) => (
  <>
    <header className="week3-header">
      <div>
        <span className="week3-eyebrow">Plan semanal</span>
        <h1>Tu semana, bien resuelta</h1>
        <p>Organiza, ajusta y prepara tus comidas desde un único lugar.</p>
      </div>
      <div className="week3-header-actions">
        <button type="button" className="week3-soft-action" onClick={onStock}><Inventory2Rounded /> Fondo</button>
        <button type="button" className="week3-soft-action" onClick={onBatch}><KitchenRounded /> Batch cooking</button>
        <button type="button" className="week3-primary-action" onClick={onGenerate}><AutoAwesomeRounded /> Generar con IA</button>
      </div>
    </header>
    <div className="week3-mobile-actions" aria-label="Acciones del plan semanal">
      <button type="button" onClick={onGenerate}><AutoAwesomeRounded /><span><strong>Generar</strong><small>con IA</small></span></button>
      <button type="button" onClick={onStock}><Inventory2Rounded /><span><strong>Fondo</strong><small>Ver reservas</small></span></button>
      <button type="button" onClick={onBatch}><KitchenRounded /><span><strong>Batch</strong><small>Cocinar antes</small></span></button>
    </div>
  </>
);

const WeekNavigator = ({ start, days, selectedKey, onSelect, actions }) => {
  const selectedTabRef = useRef(null);
  const label = `${format(start, 'd MMM', { locale: es })} – ${format(addDays(start, 6), 'd MMM yyyy', { locale: es })}`;

  useEffect(() => {
    selectedTabRef.current?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [selectedKey]);

  return (
    <section className="week3-week-nav">
      <div className="week3-week-control">
        <button type="button" onClick={actions.prevWeek} aria-label="Semana anterior"><ChevronLeftRounded /></button>
        <div><strong>{label}</strong><button type="button" onClick={actions.goToToday}><TodayRounded /> Ir a hoy</button></div>
        <button type="button" onClick={actions.nextWeek} aria-label="Semana siguiente"><ChevronRightRounded /></button>
      </div>
      <div className="week3-day-tabs" role="tablist" aria-label="Días de la semana">
        {days.map((day) => {
          const key = format(new Date(day.date), 'yyyy-MM-dd');
          const isSelected = key === selectedKey;
          const isToday = new Date(day.date).toDateString() === new Date().toDateString();
          const mealCount = Object.values(day.meals).filter((meal) => meal.items?.length).length;
          return (
            <button
              type="button"
              role="tab"
              aria-selected={isSelected}
              className={`${isSelected ? 'is-selected' : ''}${isToday ? ' is-today' : ''}`}
              ref={isSelected ? selectedTabRef : null}
              onClick={() => onSelect(key)}
              key={key}
            >
              <span>{format(new Date(day.date), 'EEE', { locale: es })}</span>
              <strong>{format(new Date(day.date), 'd')}</strong>
              <i><b style={{ width: `${(mealCount / 5) * 100}%` }} /></i>
              <small>{mealCount}/5</small>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const MacroProgress = ({ label, current, target, tone }) => {
  const exceeded = target > 0 && current > target;
  const progress = target ? Math.min((current / target) * 100, 100) : 0;
  return (
    <div className={`week3-macro week3-macro--${tone}${exceeded ? ' is-exceeded' : ''}`}>
      <div><span>{label}</span><strong>{Math.round(current)}<small> / {Math.round(target)}g</small></strong></div>
      <i><b style={{ width: `${progress}%` }} /></i>
      {exceeded && <em><WarningAmberRounded /> {Math.round(current - target)}g por encima</em>}
    </div>
  );
};

const FoodItem = ({ item, onEdit, onRemove, onMakeStock }) => {
  const navigate = useNavigate();
  const macros = item.finalMacronutrients || item.estimatedMacronutrients || item.totalMacronutrients || item.macronutrients || item;
  const image = getPreviewImage(item.mainImage || item.aiAnalysis?.imageUrl);
  const isAI = Boolean(item.aiAnalysis || item.finalMacronutrients || item.isUnplanned);
  const isStock = item.type === 'planned-stock';
  const canOpen = (item.type === 'planned-recipe' && item._id) || (isStock && item.sourceRecipeId);
  const canMakeStock = item.type === 'planned-recipe' && item._id;

  const openRecipe = () => {
    if (!canOpen) return;
    navigate(`/recetas/${isStock ? item.sourceRecipeId : item._id}`);
  };

  return (
    <article className={`week3-food${canOpen ? ' is-clickable' : ''}`} onClick={openRecipe}>
      <div className={`week3-food-image${isAI ? ' is-ai' : ''}${isStock ? ' is-stock' : ''}`}>
        {image ? <img src={image} alt="" /> : isStock ? <AcUnitRounded /> : isAI ? <AutoAwesomeRounded /> : <RestaurantRounded />}
      </div>
      <div className="week3-food-copy">
        <div><strong>{item.name || item.aiAnalysis?.dishName || 'Comida'}</strong>{isAI && <span>IA</span>}{isStock && <span>Fondo</span>}</div>
        <small>{item.quantity || 1} {item.unit || 'ración'}</small>
        <p><b>{Math.round(value(macros.kilocalories))} kcal</b><span>P {Math.round(value(macros.protein))}g</span><span>C {Math.round(value(macros.carbs))}g</span><span>G {Math.round(value(macros.fat))}g</span></p>
      </div>
      <div className="week3-food-actions">
        {canMakeStock && <button type="button" aria-label="Hacer fondo" onClick={(event) => { event.stopPropagation(); onMakeStock(item); }}><AcUnitRounded /></button>}
        {isAI && <button type="button" aria-label="Editar comida" onClick={(event) => { event.stopPropagation(); onEdit(item); }}><EditOutlined /></button>}
        <button type="button" aria-label="Eliminar comida" onClick={(event) => { event.stopPropagation(); onRemove(item._id); }}><DeleteOutlineRounded /></button>
      </div>
    </article>
  );
};

const MealBlock = ({ name, data, onAdd, onEdit, onRemove, onMakeStock }) => {
  const meta = MEAL_META[name] || { icon: RestaurantRounded, tone: 'slate', short: name };
  const Icon = meta.icon;
  const hasItems = data.items?.length > 0;
  return (
    <section className={`week3-meal week3-meal--${meta.tone}`}>
      <header>
        <div className="week3-meal-icon"><Icon /></div>
        <div><h3>{meta.short}</h3><span>{Math.round(value(data.macros?.kilocalories))} kcal</span></div>
        <button type="button" onClick={onAdd}><AddRounded /> Añadir</button>
      </header>
      <div className="week3-meal-items">
        {hasItems ? data.items.map((item, index) => (
          <FoodItem
            item={item}
            key={item._id || index}
            onEdit={(current) => onEdit({ ...current, meal: current.meal || name })}
            onRemove={onRemove}
            onMakeStock={(current) => onMakeStock({ ...current, meal: current.meal || name })}
          />
        )) : (
          <button className="week3-empty-meal" type="button" onClick={onAdd}><AddRounded /><span><strong>Planificar {meta.short.toLowerCase()}</strong><small>Añade una receta, alimento o registro rápido</small></span></button>
        )}
      </div>
    </section>
  );
};

const DayWorkspace = ({ day, goals, onAdd, onEdit, onRemove, onMakeStock }) => {
  if (!day) return null;
  const date = new Date(day.date);
  const planned = Object.values(day.meals).filter((meal) => meal.items?.length).length;
  const caloriesExceeded = value(day.totals.kilocalories) > value(goals.kilocalories);
  const caloriesRemaining = Math.abs(value(goals.kilocalories) - value(day.totals.kilocalories));
  const calorieProgress = value(goals.kilocalories) ? Math.min((value(day.totals.kilocalories) / value(goals.kilocalories)) * 100, 100) : 0;
  const dateKey = format(date, 'yyyy-MM-dd');
  const rawDateLabel = format(date, "EEEE, d 'de' MMMM", { locale: es });
  const dateLabel = rawDateLabel.charAt(0).toUpperCase() + rawDateLabel.slice(1);
  return (
    <main className="week3-day-workspace">
      <div className="week3-day-heading">
        <div>
          <span>{planned === 5 ? <CheckCircleRounded /> : <RestaurantRounded />} {planned} de 5 comidas planificadas</span>
          <h2>{dateLabel}</h2>
        </div>
        <div className={`week3-calorie-total${caloriesExceeded ? ' is-exceeded' : ''}`}>
          <div><span>Calorías</span><strong>{Math.round(value(day.totals.kilocalories))}<small> / {Math.round(value(goals.kilocalories))}</small></strong></div>
          <i><b style={{ width: `${calorieProgress}%` }} /></i>
          <em>{caloriesExceeded && <WarningAmberRounded />}{Math.round(caloriesRemaining)} kcal {caloriesExceeded ? 'por encima' : 'disponibles'}</em>
        </div>
      </div>
      <div className="week3-daily-macros">
        <MacroProgress label="Proteína" current={value(day.totals.protein)} target={value(goals.protein)} tone="protein" />
        <MacroProgress label="Carbohidratos" current={value(day.totals.carbs)} target={value(goals.carbs)} tone="carbs" />
        <MacroProgress label="Grasas" current={value(day.totals.fat)} target={value(goals.fat)} tone="fat" />
      </div>
      <div className="week3-meal-grid">
        {Object.entries(day.meals).map(([name, data]) => (
          <MealBlock
            name={name}
            data={data}
            key={name}
            onAdd={() => onAdd(dateKey, name)}
            onEdit={(item) => onEdit({ ...item, date: item.date || dateKey, meal: item.meal || name })}
            onRemove={(id) => onRemove(dateKey, name, id)}
            onMakeStock={(item) => onMakeStock(dateKey, name, item)}
          />
        ))}
      </div>
    </main>
  );
};

const CompactWeekSummary = ({ totals, goals, days }) => {
  const weeklyGoal = getWeeklyGoal(goals);
  const progress = weeklyGoal.kilocalories ? Math.min((value(totals.kilocalories) / weeklyGoal.kilocalories) * 100, 100) : 0;
  const completeDays = days.filter((day) => Object.values(day.meals).filter((meal) => meal.items?.length).length >= 4).length;
  return (
    <section className="week3-mobile-summary">
      <div className="week3-mobile-summary-head">
        <span>Resumen semanal</span>
        <strong>{Math.round(value(totals.kilocalories)).toLocaleString('es-ES')}<small> / {weeklyGoal.kilocalories.toLocaleString('es-ES')} kcal</small></strong>
      </div>
      <i><b style={{ width: `${progress}%` }} /></i>
      <div className="week3-mobile-summary-macros">
        <span className="is-protein"><b>P</b><strong>{Math.round(value(totals.protein))}g</strong><small>/{weeklyGoal.protein}g</small></span>
        <span className="is-carbs"><b>C</b><strong>{Math.round(value(totals.carbs))}g</strong><small>/{weeklyGoal.carbs}g</small></span>
        <span className="is-fat"><b>G</b><strong>{Math.round(value(totals.fat))}g</strong><small>/{weeklyGoal.fat}g</small></span>
        <em><CheckCircleRounded /> {completeDays}/7 días casi completos</em>
      </div>
    </section>
  );
};

const WeekSummary = ({ totals, goals, days }) => {
  const weeklyGoal = getWeeklyGoal(goals);
  const calorieProgress = weeklyGoal.kilocalories ? Math.min((value(totals.kilocalories) / weeklyGoal.kilocalories) * 100, 100) : 0;
  const completeDays = days.filter((day) => Object.values(day.meals).filter((meal) => meal.items?.length).length >= 4).length;
  return (
    <aside className="week3-aside">
      <section className="week3-summary-card">
        <span className="week3-eyebrow">Resumen semanal</span>
        <h2>Cómo va tu plan</h2>
        <div className="week3-week-ring">
          <svg viewBox="0 0 112 112"><circle cx="56" cy="56" r="46" /><circle className="is-progress" cx="56" cy="56" r="46" strokeDasharray="289" strokeDashoffset={289 - (289 * calorieProgress) / 100} /></svg>
          <div><strong>{Math.round(value(totals.kilocalories)).toLocaleString('es-ES')}</strong><span>de {weeklyGoal.kilocalories.toLocaleString('es-ES')} kcal</span></div>
        </div>
        <div className="week3-summary-macros">
          <MacroProgress label="Proteína" current={value(totals.protein)} target={weeklyGoal.protein} tone="protein" />
          <MacroProgress label="Carbohidratos" current={value(totals.carbs)} target={weeklyGoal.carbs} tone="carbs" />
          <MacroProgress label="Grasas" current={value(totals.fat)} target={weeklyGoal.fat} tone="fat" />
        </div>
        <div className="week3-completion"><CheckCircleRounded /><span><strong>{completeDays} días</strong> casi completos esta semana</span></div>
      </section>
    </aside>
  );
};

const WeeklyPlannerV3 = ({ preview = false }) => {
  const auth = useAuth();
  const liveData = useWeeklyMenu(auth);
  const previewData = useMemo(() => createPreviewMenu(), []);
  const { currentWeekStart, weeklyData, weeklyTotals, userGoals, loading, error, actions } = preview ? previewData : liveData;
  const days = useMemo(() => Object.values(weeklyData).sort((a, b) => new Date(a.date) - new Date(b.date)), [weeklyData]);
  const [selectedKey, setSelectedKey] = useState('');
  const [popupState, setPopupState] = useState({ open: false, date: null, meal: null });
  const [aiModalState, setAiModalState] = useState({ open: false, date: null, meal: null, editingMeal: null });
  const [showMenuGenerator, setShowMenuGenerator] = useState(false);
  const [showFoodStock, setShowFoodStock] = useState(false);
  const [batchModal, setBatchModal] = useState({ open: false, mode: 'list', range: null });
  const [stockDialog, setStockDialog] = useState({ open: false, date: null, meal: null, item: null, portions: 1, storage: 'freezer', expiresAt: '', notes: '' });

  useEffect(() => {
    if (!days.length) return;
    const todayKey = format(new Date(), 'yyyy-MM-dd');
    setSelectedKey((currentKey) => {
      // Meal edits replace weeklyData during optimistic updates. Keep the day the
      // user is working on unless navigation loaded a week that no longer has it.
      if (currentKey && weeklyData[currentKey]) return currentKey;
      return weeklyData[todayKey] ? todayKey : format(new Date(days[0].date), 'yyyy-MM-dd');
    });
  }, [currentWeekStart, days, weeklyData]);

  const selectedDay = weeklyData[selectedKey] || days[0];
  const buildDate = (dateKey) => dateKey ? new Date(`${dateKey}T12:00:00`) : new Date();
  const closeAI = () => setAiModalState({ open: false, date: null, meal: null, editingMeal: null });

  const saveStock = async () => {
    await actions.makeRecipeStock(stockDialog.date, stockDialog.meal, stockDialog.item, {
      portions: Number(stockDialog.portions), storage: stockDialog.storage, expiresAt: stockDialog.expiresAt, notes: stockDialog.notes,
    });
    setStockDialog((current) => ({ ...current, open: false }));
  };

  if (loading) return <div className="week3-loading"><CircularProgress size={30} /><span>Preparando tu semana…</span></div>;
  if (error) return <Box p={3}><Alert severity="error">{error}</Alert></Box>;

  return (
    <ExperimentalPopupProvider>
      <Box className="week3-shell">
      <div className="week3-page">
        <PlannerHeader onGenerate={() => setShowMenuGenerator(true)} onStock={() => setShowFoodStock(true)} onBatch={() => setBatchModal({ open: true, mode: 'list', range: null })} />
        <CompactWeekSummary totals={weeklyTotals} goals={userGoals} days={days} />
        <WeekNavigator start={currentWeekStart} days={days} selectedKey={selectedKey} onSelect={setSelectedKey} actions={actions} />
        <div className="week3-layout">
          <DayWorkspace
            day={selectedDay}
            goals={userGoals}
            onAdd={(date, meal) => setPopupState({ open: true, date, meal })}
            onEdit={(item) => setAiModalState({ open: true, date: item.date, meal: item.meal, editingMeal: item })}
            onRemove={actions.removeFoodFromMeal}
            onMakeStock={(date, meal, item) => setStockDialog({ open: true, date, meal, item, portions: item.stockPrepPortions || 1, storage: item.stockPreparation?.storage || 'freezer', expiresAt: item.stockPreparation?.expiresAt?.split('T')[0] || '', notes: item.stockPreparation?.notes || '' })}
          />
          <WeekSummary totals={weeklyTotals} goals={userGoals} days={days} />
        </div>
      </div>

      <AddMealPopup open={popupState.open} onClose={() => setPopupState((current) => ({ ...current, open: false }))} onSelect={async (selection) => {
        if (!popupState.date || !popupState.meal) return;
        await actions.addFoodToMeal(popupState.date, popupState.meal, selection);
      }} />
      <AIFoodAnalyzer open={aiModalState.open} onClose={closeAI} selectedDate={buildDate(aiModalState.date)} selectedMeal={aiModalState.meal} existingMeal={aiModalState.editingMeal} onSuccess={async () => { await actions.refresh(); closeAI(); }} />

      <Dialog open={stockDialog.open} onClose={() => setStockDialog((current) => ({ ...current, open: false }))} fullWidth maxWidth="xs">
        <DialogTitle>Preparar para fondo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">{stockDialog.item?.name}</Typography>
            <Tabs value={stockDialog.storage} onChange={(_, storage) => setStockDialog((current) => ({ ...current, storage, expiresAt: storage === 'freezer' ? '' : current.expiresAt }))}>
              <Tab value="freezer" icon={<AcUnitRounded />} iconPosition="start" label="Congelador" />
              <Tab value="fridge" icon={<Inventory2Rounded />} iconPosition="start" label="Nevera" />
            </Tabs>
            <TextField label="Raciones" type="number" value={stockDialog.portions} inputProps={{ min: 0.5, step: 0.5 }} onChange={(event) => setStockDialog((current) => ({ ...current, portions: event.target.value }))} fullWidth />
            {stockDialog.storage === 'fridge' && <TextField label="Caducidad" type="date" value={stockDialog.expiresAt} onChange={(event) => setStockDialog((current) => ({ ...current, expiresAt: event.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />}
            <TextField label="Notas" value={stockDialog.notes} onChange={(event) => setStockDialog((current) => ({ ...current, notes: event.target.value }))} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setStockDialog((current) => ({ ...current, open: false }))}>Cancelar</Button><Button variant="contained" onClick={saveStock} disabled={!stockDialog.item || Number(stockDialog.portions) <= 0}>Guardar fondo</Button></DialogActions>
      </Dialog>

      <Modal open={showMenuGenerator} onClose={() => setShowMenuGenerator(false)} className="week3-modal"><div className="week3-modal-content"><IconButton aria-label="Cerrar generador" onClick={() => setShowMenuGenerator(false)} className="week3-modal-close"><CloseRounded /></IconButton><MenuGeneratorIA weeklyData={weeklyData} initialWeekStart={currentWeekStart} onClose={() => setShowMenuGenerator(false)} onMenuSaved={async (info) => { await actions.refresh(); setShowMenuGenerator(false); if (info?.batchCookingRequested && info?.weekStartDate) setBatchModal({ open: true, mode: 'create', range: { start: info.weekStartDate, end: format(addDays(new Date(`${info.weekStartDate}T12:00:00`), 4), 'yyyy-MM-dd') } }); }} /></div></Modal>
      <Modal open={showFoodStock} onClose={() => setShowFoodStock(false)} className="week3-modal"><div className="week3-modal-content"><IconButton aria-label="Cerrar fondo" onClick={() => setShowFoodStock(false)} className="week3-modal-close"><CloseRounded /></IconButton><FoodStockPage /></div></Modal>
        <BatchCookingModal open={batchModal.open} onClose={() => setBatchModal({ open: false, mode: 'list', range: null })} currentWeekStart={currentWeekStart} initialMode={batchModal.mode} initialRange={batchModal.range} />
      </Box>
    </ExperimentalPopupProvider>
  );
};

export default WeeklyPlannerV3;
